import { useRef, useState } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { googleFormConfig, isGoogleFormConfigured } from '../config/googleForm';
import "./pages.css"

const initialFormData = {
    name: '',
    email: '',
    phone: '',
    message: ''
};

const initialTouchedState = {
    name: false,
    email: false,
    phone: false,
    message: false
};

const validateField = (name, value) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return `${name.charAt(0).toUpperCase() + name.slice(1)} is required.`;
    }

    if (name === 'name' && trimmedValue.length < 3) {
        return 'Please enter at least 3 characters.';
    }

    if (name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
        return 'Please enter a valid email address.';
    }

    if (name === 'phone' && !/^\+?[0-9\s()-]{10,15}$/.test(trimmedValue)) {
        return 'Please enter a valid phone number.';
    }

    return '';
};

const ContactUs = () => {
    const titleRef = useRef(null);
    const textRef = useRef(null);
    const formContainerRef = useRef(null);
    const formRef = useRef(null);
    const btnRef = useRef(null);
    const footerRef = useRef(null);

    const [formData, setFormData] = useState(initialFormData);
    const [touched, setTouched] = useState(initialTouchedState);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useScrollAnimation([titleRef, textRef, formContainerRef, btnRef, footerRef]);

    const errors = {
        name: validateField('name', formData.name),
        email: validateField('email', formData.email),
        phone: validateField('phone', formData.phone)
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({ ...prev, [name]: value }));
        setStatus({ type: '', message: '' });
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched((prev) => ({ ...prev, [name]: true }));
    };

    const handleSubmit = (e) => {
        const nextTouchedState = {
            name: true,
            email: true,
            phone: true,
            message: true
        };

        setTouched(nextTouchedState);

        if (!isGoogleFormConfigured()) {
            e.preventDefault();
            setStatus({
                type: 'error',
                message: 'Google Form is not configured yet. Add the form URL and entry ids in src/config/googleForm.js.'
            });
            return;
        }

        if (Object.values(errors).some(Boolean)) {
            e.preventDefault();
            setStatus({
                type: 'error',
                message: 'Please fix the highlighted fields before submitting.'
            });
            return;
        }

        setIsSubmitting(true);
        setStatus({
            type: '',
            message: ''
        });
    };

    const handleIframeLoad = () => {
        if (!isSubmitting) {
            return;
        }

        setIsSubmitting(false);
        setStatus({
            type: 'success',
            message: 'Inquiry submitted successfully.'
        });
        setFormData(initialFormData);
        setTouched(initialTouchedState);
    };

    const getFieldClassName = (fieldName) =>
        `contact-input${fieldName === 'message' ? ' contact-textarea' : ''}${touched[fieldName] && errors[fieldName] ? ' contact-input-error' : ''}`;

    return (
        <div className="max-width-about-class main-class">
            <p ref={titleRef} className='get-in-touch-text-contact animate-on-scroll'>Get In Touch</p>
            <div className='line-separator-contact-page'></div>

            <div className="contactus-div">
                <p ref={textRef} className="aboutus-text-details animate-on-scroll">We would love to hear from you. Whether you're interested in booking a consultation, have a question about our services, or simply want to learn more about our brand, please feel free to reach out.</p>
                <form
                    ref={formRef}
                    action={googleFormConfig.formAction || undefined}
                    method="POST"
                    target="hidden-google-form-frame"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div ref={formContainerRef} className="input-div animate-on-scroll">
                        <div className="form-left">
                            <div className="contact-field-group">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Name"
                                    className={getFieldClassName('name')}
                                    value={formData.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    autoComplete="name"
                                />
                                {touched.name && errors.name && (
                                    <p className="contact-validation-text contact-inline-error">{errors.name}</p>
                                )}
                            </div>
                            <div className="contact-field-group">
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email"
                                    className={getFieldClassName('email')}
                                    value={formData.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    autoComplete="email"
                                />
                                {touched.email && errors.email && (
                                    <p className="contact-validation-text contact-inline-error">{errors.email}</p>
                                )}
                            </div>
                            <div className="contact-field-group">
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Contact No."
                                    className={getFieldClassName('phone')}
                                    value={formData.phone}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    autoComplete="tel"
                                    maxLength={10}
                                />
                                {touched.phone && errors.phone && (
                                    <p className="contact-validation-text contact-inline-error">{errors.phone}</p>
                                )}
                            </div>
                        </div>
                        <div className="form-right">
                            <div className="contact-field-group contact-field-group-message">
                                <textarea
                                    name="message"
                                    placeholder="Message"
                                    className={getFieldClassName('message')}
                                    value={formData.message}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <input type="hidden" name={googleFormConfig.entryIds.name || undefined} value={formData.name} />
                    <input type="hidden" name={googleFormConfig.entryIds.email || undefined} value={formData.email} />
                    <input type="hidden" name={googleFormConfig.entryIds.phone || undefined} value={formData.phone} />
                    <input type="hidden" name={googleFormConfig.entryIds.message || undefined} value={formData.message} />

                    <div className="contact-feedback-slot">
                        {status.message && (
                            <p className={`contact-status-text contact-feedback-text ${status.type === 'error' ? 'contact-status-error' : 'contact-status-success'}`}>
                                {status.message}
                            </p>
                        )}
                    </div>

                    <div ref={btnRef} className="get-in-touch-btn-wrapper2 animate-on-scroll">
                        <button type="submit" className="hero-btn2" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </form>

                <iframe
                    title="hidden-google-form-frame"
                    name="hidden-google-form-frame"
                    style={{ display: 'none' }}
                    onLoad={handleIframeLoad}
                />
                <div ref={footerRef} className='contact-title-div animate-on-scroll'>

                    <p className='contactus-italic-title' >Tailored to Perfection</p>
                    <div className='separator-div'>
                        <div className='separator-line'></div>
                        <div className='separator-line'></div>
                    </div>
                </div>
            </div>


        </div>
    );
};

export default ContactUs;
